// Define interfaces for all data types
export interface Customer {
  id: string;
  name: string;
  email: string;
  status: 'Active' | 'Pending' | 'Suspended';
  plan: string;
  revenue: string;
  products: string[];
  createTime: string;
  // Additional fields needed for CustomerDetail
  phone?: string;
  contactPerson?: string;
  address?: string;
  country?: string;
  notes?: string;
  services?: Array<{
    id: string;
    name: string;
    product: string;
    status: string;
    usage: string;
  }>;
  invoices?: Array<{
    id: string;
    date: string;
    amount: string;
    status: string;
  }>;
  activity?: Array<{
    date: string;
    action: string;
    user: string;
  }>;
}

export interface ResellerStats {
  totalCustomers: number;
  activeCustomers: number;
  totalRevenue: number;
  commissionRate: number;
}

export interface EnrollResellerResponse {
  success: boolean;
  message: string;
}

export interface CustomerActionResponse {
  success: boolean;
  message?: string;
}

export interface EnrollResellerData {
  businessName: string;
  businessType: string;
  website: string;
  yearFounded: string;
  employeeCount: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  acceptTerms: boolean;
  marketingConsent: boolean;
}

// Mock customer data - replace with API calls in production
export const getCustomers = (): Customer[] => {
  try {
    // In real implementation, this would fetch from an API
    return [
      {
        id: '1',
        name: 'Acme Corporation',
        email: 'contact@acme.com',
        status: 'Active',
        plan: 'Enterprise',
        revenue: '$28,500',
        products: ['Cloud Server', 'VPS', 'Storage'],
        createTime: '2023-05-15 09:30:00'
      },
      {
        id: '2',
        name: 'TechNova Solutions',
        email: 'info@technova.com',
        status: 'Active',
        plan: 'Business',
        revenue: '$16,750',
        products: ['Cloud Server', 'Storage'],
        createTime: '2023-07-22 14:15:00'
      },
      {
        id: '3',
        name: 'Global Systems Inc.',
        email: 'support@globalsystems.com',
        status: 'Pending',
        plan: 'Business',
        revenue: '$9,200',
        products: ['VPS', 'Storage', 'CDN'],
        createTime: '2024-01-10 11:45:00'
      },
      {
        id: '4',
        name: 'Infinite Data',
        email: 'sales@infinitedata.com',
        status: 'Active',
        plan: 'Enterprise',
        revenue: '$32,100',
        products: ['Cloud Server', 'VPS', 'CDN'],
        createTime: '2023-09-05 10:20:00'
      },
      {
        id: '5',
        name: 'BrightStream Media',
        email: 'info@brightstream.com',
        status: 'Suspended',
        plan: 'Business',
        revenue: '$5,800',
        products: ['CDN', 'Storage'],
        createTime: '2023-11-18 16:30:00'
      }
    ];
  } catch (error) {
    console.error('Error fetching customers:', error);
    throw error;
  }
};

export const getCustomerById = (id: string): Customer | null => {
  try {
    const customers = getCustomers();
    return customers.find(customer => customer.id === id) || null;
  } catch (error) {
    console.error(`Error fetching customer with ID ${id}:`, error);
    throw error;
  }
};

export const getResellerStats = (): ResellerStats => {
  try {
    const customers = getCustomers();
    
    return {
      totalCustomers: customers.length,
      activeCustomers: customers.filter(c => c.status === 'Active').length,
      totalRevenue: customers.reduce((sum, c) => sum + parseInt(c.revenue.replace(/[^0-9]/g, '')), 0),
      commissionRate: 0.25,
    };
  } catch (error) {
    console.error('Error calculating reseller stats:', error);
    throw error;
  }
};

export const enrollReseller = async (_formData: EnrollResellerData): Promise<EnrollResellerResponse> => {
  try {
    // Mock implementation - replace with actual API call
    return new Promise((resolve) => {
      setTimeout(() => {
        // Simulate a successful enrollment
        resolve({ success: true, message: 'Reseller enrolled successfully' });
        
        // Or simulate an error with:
        // resolve({ success: false, message: 'Unable to enroll. Please try again.' });
      }, 1500);
    });
  } catch (error) {
    console.error('Error enrolling reseller:', error);
    return { 
      success: false, 
      message: 'An unexpected error occurred. Please try again later.' 
    };
  }
};

export const suspendCustomer = async (id: string): Promise<CustomerActionResponse> => {
  try {
    // Mock implementation - replace with actual API call
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, message: 'Customer suspended successfully' });
      }, 500);
    });
  } catch (error) {
    console.error(`Error suspending customer with ID ${id}:`, error);
    return { 
      success: false, 
      message: 'Failed to suspend customer. Please try again.' 
    };
  }
};

export const upgradeCustomerPlan = async (id: string, plan: string): Promise<CustomerActionResponse> => {
  try {
    // Mock implementation - replace with actual API call
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ success: true, message: `Customer upgraded to ${plan} plan successfully` });
      }, 500);
    });
  } catch (error) {
    console.error(`Error upgrading customer ${id} to plan ${plan}:`, error);
    return { 
      success: false, 
      message: 'Failed to upgrade customer plan. Please try again.' 
    };
  }
}; 